import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  EmailIcon,
  PasswordIcon,
  ErrorIcon,
  EyeOpenIcon,
  EyeClosedIcon,
} from '../../components/icons/Icons';
import GeometricCharacters from './components/GeometricCharacters/GeometricCharacters';
import './Login.css';

export default function Login() {
  const [identifier, setIdentifier] = useState(''); // Pode ser email ou username
  const [password, setPassword] = useState('');
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [charactersEmotion, setCharactersEmotion] = useState('neutral');

  // Lógica para determinar a logo baseada no domínio
  const domainLogos = process.env.REACT_APP_DOMAIN_LOGOS ? process.env.REACT_APP_DOMAIN_LOGOS.split(';').reduce((acc, pair) => {
    const [domain, logo] = pair.split('=');
    acc[domain.trim()] = logo.trim();
    return acc;
  }, {}) : {};
  const currentDomain = window.location.origin;
  const logoSrc = domainLogos[currentDomain] || '/logo.png';

  const REMEMBERED_IDENTIFIER_KEY = 'rememberedIdentifier';

  useEffect(() => {
    const rememberedIdentifier = localStorage.getItem(REMEMBERED_IDENTIFIER_KEY);
    if (rememberedIdentifier) {
      console.log('Identificador armazenado:', rememberedIdentifier);
      setIdentifier(rememberedIdentifier);
      setRememberMe(true);
    }
  }, []);

  const handleIdentifierFocus = () => {
    setCharactersEmotion('surprise');
  };

  const handleIdentifierBlur = () => {
    setCharactersEmotion('neutral');
  };

  const handlePasswordChange = e => {
    setPassword(e.target.value);
    if (e.target.value.length > 0) {
      setCharactersEmotion('shy');
    } else {
      setCharactersEmotion('neutral');
    }
  };

  const handlePasswordBlur = () => {
    setCharactersEmotion('neutral');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setCharactersEmotion('neutral'); // Reset emoção no início
    try {
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_IDENTIFIER_KEY, identifier);
      } else {
        localStorage.removeItem(REMEMBERED_IDENTIFIER_KEY);
      }

      await login(identifier, password, rememberMe);

      // Login bem-sucedido - personagens felizes
      setCharactersEmotion('happy');

      // Aguarda um pouco para mostrar a emoção antes de navegar
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
    } catch (err) {
      const message = err.response?.data?.msg || 'Ocorreu um erro ao fazer login.';
      setError(message);
      // Erro no login - personagens tristes
      setCharactersEmotion('sad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      <div className="login-wrapper">
        {/* Characters Section */}
        <div className="characters-section">
          <GeometricCharacters emotion={charactersEmotion} />
        </div>

        {/* Login Section */}
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="logo">
                <img
                  src={logoSrc}
                  alt="LMS Platform Logo"
                  style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                />
              </div>
              <h1 className="login-title">Bem-vindo de volta</h1>
              <p className="login-subtitle">Entre na sua conta para continuar aprendendo</p>
            </div>

            <form className="login-form" onSubmit={handleSubmit}>
              {error && (
                <div className="error-message" role="alert">
                  <ErrorIcon />
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="identifier" className="form-label">
                  Email ou Nome de Usuário
                </label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <EmailIcon />
                  </div>
                  <input
                    id="identifier"
                    type="text"
                    className="form-input"
                    placeholder="Digite seu email ou nome de usuário"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    onFocus={handleIdentifierFocus}
                    onBlur={handleIdentifierBlur}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Senha
                </label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <PasswordIcon />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Digite sua senha"
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={handlePasswordBlur}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                    disabled={loading}
                  >
                    {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                  </button>
                </div>
              </div>

              <div className="form-options">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">Lembrar de mim</span>
                </label>
                {/*
              <Link to="/forgot-password" className="forgot-password" aria-disabled={loading}>
                Esqueceu a senha?
              </Link>
              */}
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? (
                  <span className="loading-content" aria-live="polite" aria-busy="true">
                    <div className="loading-spinner"></div>
                  </span>
                ) : (
                  <span
                    className="login-content"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <span>Entrar</span>
                    {/* Ícone de seta para a direita */}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </span>
                )}
              </button>
            </form>

            {/*   Habilitar se o registro for permitido

          <div className="login-footer">
            <p>Não tem uma conta? 
              <Link to="/register" className="register-link">Cadastre-se aqui</Link>
            </p>
          </div>
          
          */}
          </div>
        </div>
      </div>
    </div>
  );
}
